import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { AllSelection, TextSelection } from '@tiptap/pm/state';

import { isZoneCell, readOpenHeaderFooter } from 'containers/Tiptap/lib/header-footer-zone';

export const HEADER_FOOTER_CLOSE_ATTR = 'data-header-footer-close';

interface CaretCell {
  index: number;
  count: number;
  rowDepth: number;
}

export const getCaretCell = (editor: Editor): CaretCell | null => {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if (isZoneCell($from.node(depth))) {
      return { index: $from.index(depth - 1), count: $from.node(depth - 1).childCount, rowDepth: depth - 1 };
    }
  }
  return null;
};

const moveToCell = (editor: Editor, cell: CaretCell, index: number): void => {
  const { $from } = editor.state.selection;
  const row = $from.node(cell.rowDepth);
  let pos = $from.start(cell.rowDepth);
  for (let i = 0; i < index; i += 1) pos += row.child(i).nodeSize;
  const end = pos + row.child(index).nodeSize - 1;
  const selection = TextSelection.near(editor.state.doc.resolve(end), -1);
  editor.view.dispatch(editor.state.tr.setSelection(selection));
  editor.view.focus();
};

const clearZoneCells = (editor: Editor): void => {
  const { doc, schema } = editor.state;
  const cells: { from: number; to: number }[] = [];
  doc.descendants((node, pos) => {
    if (!isZoneCell(node)) return true;
    cells.push({ from: pos + 1, to: pos + node.nodeSize - 1 });
    return false;
  });
  const tr = editor.state.tr;
  for (const { from, to } of cells.reverse()) tr.replaceWith(from, to, schema.nodes.paragraph.create());
  tr.setSelection(TextSelection.near(tr.doc.resolve(0), 1));
  editor.view.dispatch(tr);
};

const OPEN_POPUP_SELECTOR = '[role="menu"], [role="listbox"], [role="dialog"], [data-radix-popper-content-wrapper]';

/**
 * Keyboard while a header or footer is open:
 * - Select-all covers both zones, not just the cell the table extension would stop at.
 * - Backspace or Delete over a select-all empties the cells and keeps the table.
 * - Tab moves from the left zone to the right zone, then on to the panel's close button; Shift+Tab
 *   moves back. Escape closes the header or footer and its panel wherever focus is.
 */
export const useHeaderFooterKeys = (bodyEditor: Editor, isOpen: boolean): void => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const onTab = (event: KeyboardEvent): void => {
      const zoneEditor = readOpenHeaderFooter(bodyEditor)?.editor;
      if (!zoneEditor || !zoneEditor.view.dom.contains(event.target as Node)) return;

      event.preventDefault();
      event.stopPropagation();
      const cell = getCaretCell(zoneEditor);
      if (event.shiftKey) {
        if (cell && cell.index > 0) moveToCell(zoneEditor, cell, cell.index - 1);
        return;
      }
      if (cell && cell.index < cell.count - 1) moveToCell(zoneEditor, cell, cell.index + 1);
      else document.querySelector<HTMLElement>(`[${HEADER_FOOTER_CLOSE_ATTR}]`)?.focus();
    };

    const onSelectAll = (event: KeyboardEvent): void => {
      const zoneEditor = readOpenHeaderFooter(bodyEditor)?.editor;
      if (!zoneEditor || !zoneEditor.view.dom.contains(event.target as Node)) return;

      event.preventDefault();
      event.stopPropagation();
      zoneEditor.view.dispatch(zoneEditor.state.tr.setSelection(new AllSelection(zoneEditor.state.doc)));
    };

    const onDelete = (event: KeyboardEvent): void => {
      const zoneEditor = readOpenHeaderFooter(bodyEditor)?.editor;
      if (!zoneEditor || !zoneEditor.view.dom.contains(event.target as Node)) return;
      if (!(zoneEditor.state.selection instanceof AllSelection)) return;

      event.preventDefault();
      event.stopPropagation();
      clearZoneCells(zoneEditor);
    };

    const onEscape = (event: KeyboardEvent): void => {
      const zoneEditor = readOpenHeaderFooter(bodyEditor)?.editor;
      // Inside the open editor, Pages' own Escape handling closes it.
      if (!zoneEditor || zoneEditor.view.dom.contains(event.target as Node)) return;
      if (document.querySelector(OPEN_POPUP_SELECTOR)) return;
      bodyEditor.commands.closeHeaderFooterEditors();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onEscape(event);
      else if (event.key === 'Backspace' || event.key === 'Delete') onDelete(event);
      else if (event.key.toLowerCase() === 'a' && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey)
        onSelectAll(event);
      else if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) onTab(event);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [bodyEditor, isOpen]);
};
